// Amadeus seat-map resources. Every constant and lookup table here is
// Amadeus-shape and Amadeus-only — never imported outside the Amadeus
// provider directory. Other providers (Sabre NDC, Kyte, Travelfusion)
// surface equivalent concepts through different keys and must own their
// own per-provider resource tables alongside.

import { is_adult, is_child, is_infant } from '@liveflight/utilities/travelers';

// ─── Amadeus seat characteristic codes ───────────────────────────────────

export const SEAT_SUITABLE_FOR_INFANT = 'I';
export const SEAT_NOT_SUITABLE_FOR_INFANT = '1A';
export const SEAT_SUITABLE_FOR_CHILD = 'U';
export const SEAT_NOT_SUITABLE_FOR_CHILD = 'IE';

// Amadeus codes that report a non-seat cell (lavatory, galley, door, …).
// A seat carrying any of these is excluded from the selectable pool — it's
// blocked with blackListed=true and counted in availableSeatsRemoved.
export const notActuallySeats = {
  8: 'No seat at this location',
  AR: 'No seat - airphone',
  BA: 'No seat - bar',
  D: 'No seat - exit door',
  EL: 'No seat - emergency Exit',
  ER: 'No seat - emergency Exit',
  EX: 'No seat - emergency Exit',
  GN: 'No seat - galley',
  LA: 'No seat - lavatory',
  LG: 'No seat - luggage storage',
  SO: 'No seat - storage space',
  ST: 'No seat - stairs to upper deck',
  TA: 'No seat - table',
  701: 'No seat - access to handicapped lavatory',
  KN: 'Bulkhead, no seat',
};

// Per-carrier policy overrides — Amadeus returns seats the carrier will
// not actually sell through our pipe. Downgrade them to BLOCKED so they
// never reach the UI as selectable.
export const isBlackListedByCarrier = {
  AA: (codes = []) => codes.includes('O'),
  PC: (codes = []) => !codes.includes('CH'),
  ET: (codes = []) => codes.includes('E'),
};

// UI-facing Amadeus code → label subsets.
//   premiumSeatCharacteristics       — triggers the premium styling.
//   userKnowledgeSeatCharacteristics — surfaced as labels in the seat
//                                      popover tooltip.
const nonStandardSeatCharacteristics = {
  MV: 'Row with movie screen',
  '1A_AQC_PREMIUM_SEAT': 'Premium seat',
};

export const premiumSeatCharacteristics = {
  ...nonStandardSeatCharacteristics,
};

export const userKnowledgeSeatCharacteristics = {
  ...premiumSeatCharacteristics,
  [SEAT_SUITABLE_FOR_INFANT]: 'Seat suitable for adult with an infant',
  '1M': 'Seat with movie view',
  [SEAT_SUITABLE_FOR_CHILD]: 'Seat suitable for unaccompanied minors',
};

// Full Amadeus characteristic-code → label reference. Primarily a
// canonical lookup for debugging and future UI expansions — the runtime
// path only reads the subsets above. Kept complete so future work (cabin-
// class detection, exit-row labelling, accessibility flags) can resolve
// codes here without re-mining the Amadeus documentation.
export const seatCharacteristics = {
  ...notActuallySeats,
  ...userKnowledgeSeatCharacteristics,
  1: 'Restricted seat - General',
  2: 'Leg rest available',
  3: 'Individual video screen - Choice of movies',
  4: 'Not a window seat',
  5: 'Not an aisle seat',
  6: 'Near galley seat',
  7: 'Near toilet seat',
  9: 'Center seat (not window, not aisle)',
  10: 'Seat designated for RBD "A"',
  11: 'Seat designated for RBD "B"',
  12: 'Seat designated for RBD "C"',
  13: 'Seat designated for RBD "D"',
  14: 'Seat designated for RBD "F"',
  15: 'Seat designated for RBD "H"',
  16: 'Seat designated for RBD "J"',
  17: 'Seat designated for RBD "K"',
  18: 'Seat designated for RBD "L"',
  19: 'Seat designated for RBD "M"',
  20: 'Seat designated for RBD "P"',
  21: 'Seat designated for RBD "Q"',
  22: 'Seat designated for RBD "R"',
  23: 'Seat designated for RBD "S"',
  24: 'Seat designated for RBD "T"',
  25: 'Seat designated for RBD "V"',
  26: 'Seat designated for RBD "W"',
  27: 'Seat designated for RBD "Y"',
  A: 'Aisle seat',
  AA: 'All available aisle seats',
  AB: 'Seat adjacent to bar',
  AC: 'Seat adjacent to closet',
  AG: 'Seat adjacent to galley',
  AJ: 'Adjacent aisle seats',
  AL: 'Seat adjacent to lavatory',
  AM: 'Individual movie screen - No choice of movie selection',
  AS: 'Individual airphone',
  AT: 'Seat adjacent to table',
  AU: 'Seat adjacent to stairs to upper deck',
  AV: 'Only available seats',
  AW: 'All available window seats',
  B: 'Seat with bassinet facility',
  BK: 'Blocked Seat for preferred passenger in adjacent seat',
  C: 'Crew seat',
  CC: 'Center section seat(s)',
  CH: 'Chargeable Seat',
  CL: 'Conditional seat - contact airline',
  CS: 'Conditional seat - contact airline',
  DE: 'Deportee',
  E: 'Exit row seat',
  EA: 'Not an exit seat',
  EC: 'Electronic connection for lap top or FAX machine',
  F: 'Added seat',
  FC: 'Front of cabin class/compartment',
  G: 'Seat at forward end of cabin',
  GF: 'General facility',
  GR: 'Group seat - offered to travellers belonging to a group',
  H: 'Seat with facilities for handicapped/incapacitated passenger',
  IA: 'Inside aisle seats',
  [SEAT_NOT_SUITABLE_FOR_CHILD]: 'Seat not suitable for child',
  J: 'Rear facing seat',
  K: 'Bulkhead seat',
  KA: 'Bulkhead seat with movie screen',
  L: 'Leg space seat',
  LH: 'Restricted seat - offered on long-haul segments',
  LS: 'Left side of aircraft',
  M: 'Seat without a movie view',
  MA: 'Medically OK to travel',
  N: 'No smoking seat',
  O: 'Preferential seat',
  OW: 'Over wing seat(s)',
  PC: 'Pet cabin',
  Q: 'Seat in a quiet zone',
  RS: 'Right side of aircraft',
  S: 'Smoking seat',
  T: 'Rear/Tail section of aircraft',
  UP: 'Upper deck',
  V: 'Seat to be left vacant or offered last',
  W: 'Window seat',
  WA: 'Window and aisle together',
  X: 'No facility seat (indifferent seat)',
  Z: 'Buffer zone seat',
  [SEAT_NOT_SUITABLE_FOR_INFANT]: 'Seat not allowed for infant',
  '1B': 'Seat not allowed for medical',
  '1C': 'Seat not allowed for unaccompanied minor',
  '1D': 'Restricted recline seat',
  '1W': 'Window seat without window',
  '3A': 'Individual video screen - No choice of movie',
  '3B': 'Individual video screen - Choice of movies, games, information, etc.',
  '6A': 'In front of galley seat',
  '6B': 'Behind galley seat',
  '7A': 'In front of toilet seat',
  '7B': 'Behind toilet seat',
  700: 'Individual video screen - services unspecified',
};

// Passenger-vs-seat matching rules. `seatCharacteristicsChecks` is used to
// suggest seats to specific passenger types (e.g. highlight infant-suitable
// seats for held infants); `seatSuggestion` is the inverse — seats that
// should NOT be offered to certain passenger types.
export const seatCharacteristicsChecks = Object.fromEntries(
  Object.entries({
    [SEAT_SUITABLE_FOR_INFANT]: {
      suggest: (passenger) => is_infant(passenger) || passenger.holding,
    },
    [SEAT_SUITABLE_FOR_CHILD]: {
      suggest: is_child,
    },
  }).map(([code, elem]) => [
    code,
    { ...elem, tooltip: seatCharacteristics[code] },
  ])
);

export const seatSuggestion = Object.fromEntries(
  Object.entries({
    '1C': {
      selectable: (passenger) => !is_infant(passenger) && !is_child(passenger),
    },
    [SEAT_NOT_SUITABLE_FOR_INFANT]: {
      selectable: (passenger) => !is_infant(passenger) && !passenger.holding,
    },
    [SEAT_NOT_SUITABLE_FOR_CHILD]: {
      selectable: is_adult,
    },
  }).map(([code, elem]) => [
    code,
    { ...elem, tooltip: seatCharacteristics[code] },
  ])
);

// Amadeus characteristic code → traveler_type tokens the seat bars, the
// canonical vocabulary build_seat_restrictions writes into `restrictedFor`.
// Mirrors seatSuggestion: '1C' (unaccompanied minor) bars infants and
// children; 'IE' (not suitable for child) is adults-only, so it bars teens
// too.
export const restrictedTravelerTypesByCharacteristic = {
  [SEAT_NOT_SUITABLE_FOR_INFANT]: ['infant'],
  '1C': ['infant', 'child'],
  [SEAT_NOT_SUITABLE_FOR_CHILD]: ['child', 'teen'],
};
